/**
 * Doctor Visit hooks for managing visits and talking points
 *
 * Provides hooks for fetching, creating, and updating doctor visits
 * and their items (talking points) using TanStack Query with proper
 * caching, retry logic, and cache invalidation.
 *
 * Also provides hooks for visit prep: suggested timeframe, context, and export.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createApiClient,
  ApiClientError,
} from "@floline/api-client";
import type {
  DoctorVisitItemResponse,
  CreateDoctorVisitRequest,
  UpdateDoctorVisitRequest,
  CreateDoctorVisitItemRequest,
  UpdateDoctorVisitItemRequest,
  ListDoctorVisitsResponse,
  SuggestedTimeframeResponse,
  VisitContextResponse,
  ExportDoctorVisitRequest,
} from "@floline/api-client";
import { API_URL } from "@/env";

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

export const doctorVisitKeys = {
  all: ["doctorVisits"] as const,
  lists: () => [...doctorVisitKeys.all, "list"] as const,
  list: (filters: DoctorVisitFilters) =>
    [...doctorVisitKeys.lists(), filters] as const,
  details: () => [...doctorVisitKeys.all, "detail"] as const,
  detail: (id: string) => [...doctorVisitKeys.details(), id] as const,
  items: (visitId: string) =>
    [...doctorVisitKeys.all, "items", visitId] as const,
  suggestedTimeframe: (params: Record<string, string | undefined>) =>
    [...doctorVisitKeys.all, "suggestedTimeframe", params] as const,
  context: (visitId: string, startDate?: string, endDate?: string) =>
    [...doctorVisitKeys.all, "context", visitId, startDate, endDate] as const,
};

/** API client configured for doctor visit requests */
const apiClient = createApiClient(API_URL);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DoctorVisitFilters {
  status?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// Shared retry logic: no retry on 401/404
// ---------------------------------------------------------------------------

function shouldRetry(failureCount: number, error: Error): boolean {
  if (
    error instanceof ApiClientError &&
    (error.status === 401 || error.status === 404)
  ) {
    return false;
  }
  return failureCount < 3;
}

// ---------------------------------------------------------------------------
// useDoctorVisits – list visits with optional filters
// ---------------------------------------------------------------------------

/**
 * Hook to fetch a list of doctor visits with optional filters.
 *
 * @param filters - Optional filters for status, date range, and pagination
 */
export function useDoctorVisits(filters?: DoctorVisitFilters) {
  const query = useQuery({
    queryKey: doctorVisitKeys.list(filters ?? {}),
    queryFn: async (): Promise<ListDoctorVisitsResponse> => {
      return await apiClient.listDoctorVisits(filters);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: shouldRetry,
  });

  const limit = filters?.limit ?? 50;
  const offset = filters?.offset ?? 0;
  const total = query.data?.meta.total ?? 0;
  const hasMore = offset + limit < total;

  return {
    /** Array of doctor visits */
    visits: query.data?.data ?? [],
    /** Whether visits are currently being fetched */
    isLoading: query.isLoading,
    /** Whether the fetch failed */
    isError: query.isError,
    /** Error details */
    error: query.error,
    /** Total number of visits available */
    total,
    /** Whether there are more visits beyond current page */
    hasMore,
    /** Refetch manually */
    refetch: query.refetch,
  };
}

// ---------------------------------------------------------------------------
// useDoctorVisit – fetch single visit by ID
// ---------------------------------------------------------------------------

/**
 * Hook to fetch a single doctor visit by ID.
 *
 * @param id - Visit ID or null to disable the query
 */
export function useDoctorVisit(id: string | null) {
  const query = useQuery({
    queryKey: doctorVisitKeys.detail(id ?? ""),
    queryFn: async () => {
      if (!id) throw new Error("Visit ID is required");
      const response = await apiClient.getDoctorVisit(id);
      return response.data;
    },
    enabled: id !== null,
    staleTime: 2 * 60 * 1000,
    retry: shouldRetry,
    refetchOnMount: false,
  });

  return {
    /** Visit data, or null if not loaded */
    visit: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// ---------------------------------------------------------------------------
// useCreateDoctorVisit – create a new visit
// ---------------------------------------------------------------------------

/**
 * Hook to create a new doctor visit.
 * Invalidates visit lists on success.
 */
export function useCreateDoctorVisit() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: CreateDoctorVisitRequest) => {
      const response = await apiClient.createDoctorVisit(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: doctorVisitKeys.lists() });
    },
  });

  return {
    createVisit: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
  };
}

// ---------------------------------------------------------------------------
// useUpdateDoctorVisit – update an existing visit
// ---------------------------------------------------------------------------

/**
 * Hook to update an existing doctor visit.
 * Invalidates both detail and list queries on success.
 */
export function useUpdateDoctorVisit() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateDoctorVisitRequest;
    }) => {
      const response = await apiClient.updateDoctorVisit(id, data);
      return response.data;
    },
    onSettled: (_data, _error, variables) => {
      if (variables) {
        queryClient.invalidateQueries({
          queryKey: doctorVisitKeys.detail(variables.id),
        });
      }
      queryClient.invalidateQueries({ queryKey: doctorVisitKeys.lists() });
    },
  });

  return {
    updateVisit: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error,
  };
}

// ---------------------------------------------------------------------------
// useDoctorVisitItems – list items (talking points) for a visit
// ---------------------------------------------------------------------------

/**
 * Hook to fetch items (talking points) for a doctor visit.
 *
 * @param visitId - Visit ID or null to disable
 */
export function useDoctorVisitItems(visitId: string | null) {
  const query = useQuery({
    queryKey: doctorVisitKeys.items(visitId ?? ""),
    queryFn: async (): Promise<DoctorVisitItemResponse[]> => {
      if (!visitId) throw new Error("Visit ID is required");
      const response = await apiClient.listDoctorVisitItems(visitId);
      return response.data;
    },
    enabled: visitId !== null,
    staleTime: 60 * 1000, // 1 minute — items change more often
    retry: shouldRetry,
  });

  return {
    items: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// ---------------------------------------------------------------------------
// useCreateDoctorVisitItem – add a talking point
// ---------------------------------------------------------------------------

/**
 * Hook to create a talking point for a visit.
 * Invalidates the items list for that visit on success.
 *
 * @param visitId - Parent visit ID
 */
export function useCreateDoctorVisitItem(visitId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: CreateDoctorVisitItemRequest) => {
      const response = await apiClient.createDoctorVisitItem(visitId, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: doctorVisitKeys.items(visitId),
      });
    },
  });

  return {
    createItem: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
  };
}

// ---------------------------------------------------------------------------
// useUpdateDoctorVisitItem – update a talking point
// ---------------------------------------------------------------------------

/**
 * Hook to update a single talking point.
 * Invalidates the items list for that visit on success.
 *
 * @param visitId - Parent visit ID
 */
export function useUpdateDoctorVisitItem(visitId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({
      itemId,
      data,
    }: {
      itemId: string;
      data: UpdateDoctorVisitItemRequest;
    }) => {
      const response = await apiClient.updateDoctorVisitItem(
        visitId,
        itemId,
        data
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: doctorVisitKeys.items(visitId),
      });
    },
  });

  return {
    updateItem: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error,
  };
}

// ---------------------------------------------------------------------------
// useSuggestedTimeframe – get recommended date range for visit prep
// ---------------------------------------------------------------------------

/**
 * Hook to fetch the suggested timeframe for visit prep.
 *
 * @param options - Optional filters: visitType, clinician, visitId
 * @param enabled - Whether the query should be enabled
 */
export function useSuggestedTimeframe(
  options?: { visitType?: string; clinician?: string; visitId?: string },
  enabled = true
) {
  const query = useQuery({
    queryKey: doctorVisitKeys.suggestedTimeframe({
      visitType: options?.visitType,
      clinician: options?.clinician,
      visitId: options?.visitId,
    }),
    queryFn: async (): Promise<SuggestedTimeframeResponse> => {
      const response = await apiClient.getSuggestedTimeframe(options);
      return response.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes — doesn't change often
    retry: shouldRetry,
  });

  return {
    timeframe: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// ---------------------------------------------------------------------------
// useVisitContext – get aggregated journal data for a visit's date range
// ---------------------------------------------------------------------------

/**
 * Hook to fetch the visit prep context (aggregated data).
 *
 * @param visitId - Visit ID
 * @param options - Date range overrides
 * @param enabled - Whether the query should be enabled
 */
export function useVisitContext(
  visitId: string | null,
  options?: { startDate?: string; endDate?: string },
  enabled = true
) {
  const query = useQuery({
    queryKey: doctorVisitKeys.context(
      visitId ?? "",
      options?.startDate,
      options?.endDate
    ),
    queryFn: async (): Promise<VisitContextResponse> => {
      if (!visitId) throw new Error("Visit ID is required");
      const response = await apiClient.getVisitContext(visitId, options);
      return response.data;
    },
    enabled: enabled && visitId !== null,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: shouldRetry,
  });

  return {
    context: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

// ---------------------------------------------------------------------------
// useExportDoctorVisit – trigger visit packet export (PDF/text)
// ---------------------------------------------------------------------------

/**
 * Hook to export a doctor visit packet.
 * Returns a mutation that triggers the download.
 */
export function useExportDoctorVisit() {
  const mutation = useMutation({
    mutationFn: async ({
      visitId,
      data,
    }: {
      visitId: string;
      data: ExportDoctorVisitRequest;
    }) => {
      const response = await apiClient.exportDoctorVisit(visitId, data);
      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(
        /filename="?([^";\n]+)"?/
      );
      const filename =
        filenameMatch?.[1] ??
        `doctor-visit-summary.${data.format === "text" ? "txt" : "pdf"}`;
      return { blob, filename, format: data.format ?? "pdf" };
    },
  });

  return {
    exportVisit: mutation.mutateAsync,
    isExporting: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
